// ============================================================
// VCS.Media — Geräte-Zugriff, Preview-Stream, Audio/Video-Constraints
// ============================================================
window.VCS = window.VCS || {};

VCS.Media = class {

    // ── Konstanten ───────────────────────────────────────────

    static AUDIO_MODE_KEY      = 'audio-mode';
    static AUDIO_MODE_USER_SET = 'audio-mode-user-set';
    static NETWORK_QUALITY_KEY = 'network-quality';
    static CAM_HARD_STOP_MS    = 30000;

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        // View-Cache
        this._view = {
            previewVideo:       $('#previewVideo'),
            localVideo:         $('#localVideo'),
            previewCamOff:      $('#previewCamOff'),
            previewCamOffName:  $('#previewCamOffName'),
            camSelect:          $('#camSelect'),
            micSelect:          $('#micSelect'),
            audioModeSelect:    $('#audioModeSelect'),
            networkQualitySelect: $('#networkQualitySelect'),
            regNameInput:       $('#regNameInput'),
        };
    }

    // ── Audio-Constraints ────────────────────────────────────

    getAudioConstraints() {
        const deviceId = VCS.state.selectedMicId ? { exact: VCS.state.selectedMicId } : undefined;
        const mode     = VCS.state.audioMode;

        if (mode === 'music') {
            return { deviceId, channelCount: 2, echoCancellation: false, noiseSuppression: false, autoGainControl: false };
        }
        if (mode === 'speech') {
            return { deviceId, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true };
        }
        // standard
        return { deviceId, channelCount: 2, echoCancellation: true, noiseSuppression: true, autoGainControl: true };
    }

    // ── Video-Constraints ────────────────────────────────────

    getVideoConstraints() {
        const nq = VCS.state.networkQuality;
        const res = nq === 'low'    ? { w: 640,  h: 360  }
                  : nq === 'medium' ? { w: 960,  h: 540  }
                  : null;
        const fps = nq === 'low'    ? { ideal: 15, max: 20 }
                  : nq === 'medium' ? { ideal: 24, max: 30 }
                  :                   { ideal: 30, max: 60 };

        if (VCS.state.selectedCamId) {
            return {
                deviceId   : { exact: VCS.state.selectedCamId },
                width      : { ideal: res ? res.w : 1920 },
                height     : { ideal: res ? res.h : 1080 },
                frameRate  : fps,
                aspectRatio: { ideal: 1.7777777778 },
            };
        }
        return {
            width      : { ideal: res ? res.w : 1280 },
            height     : { ideal: res ? res.h : 720  },
            frameRate  : fps,
            aspectRatio: { ideal: 1.7777777778 },
            facingMode : 'user',
        };
    }

    // ── Preview-Init ─────────────────────────────────────────

    async initPreview() {
        VCS.blur?.stopPipeline?.();
        VCS.audioMonitor?.stopPreview?.();
        this._clearCamHardStopTimer();
        VCS.state.camHardStopped = false;

        if (VCS.state.baseStream) {
            VCS.state.baseStream.getTracks().forEach(t => t.stop());
        }
        VCS.state.baseStream = null;

        const constraints = {
            video: this.getVideoConstraints(),
            audio: this.getAudioConstraints(),
        };

        try {
            VCS.state.baseStream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
            if (VCS.state.selectedCamId || VCS.state.selectedMicId) {
                console.warn('Gespeichertes Gerät nicht verfügbar, versuche Standard-Geräte:', err);
                if (VCS.state.selectedCamId) {
                    VCS.state.selectedCamId = null;
                    localStorage.removeItem('last-cam-id');
                    constraints.video = this.getVideoConstraints();
                }
                if (VCS.state.selectedMicId) {
                    VCS.state.selectedMicId = null;
                    localStorage.removeItem('last-mic-id');
                    constraints.audio = this.getAudioConstraints();
                }
                try {
                    VCS.state.baseStream = await navigator.mediaDevices.getUserMedia(constraints);
                } catch (err2) {
                    console.error('getUserMedia fehlgeschlagen:', err2);
                    VCS.ui?.showToast?.(t('mediaError'), 'error');
                    return;
                }
            } else {
                console.error('getUserMedia fehlgeschlagen:', err);
                VCS.ui?.showToast?.(t('mediaError'), 'error');
                return;
            }
        }

        VCS.state.baseStream.getAudioTracks().forEach(tr => { tr.enabled = !VCS.state.audioMuted; });
        VCS.state.baseStream.getVideoTracks().forEach(tr => { tr.enabled = !VCS.state.videoOff; });

        VCS.state.localStream = VCS.state.baseStream;
        await VCS.blur?.rebuild?.();
        VCS.audioMonitor?.startPreview?.(VCS.state.baseStream);
        await this._populateDevices();
    }

    // ── Kamera-Hard-Stop ─────────────────────────────────────

    scheduleCamHardStop() {
        this._clearCamHardStopTimer();
        if (!VCS.state.videoOff || !VCS.state.camOffByButton) {
            return;
        }
        VCS.state.camOffStopTimer = setTimeout(() => {
            VCS.state.camOffStopTimer = null;
            if (!VCS.state.videoOff || !VCS.state.camOffByButton) {
                return;
            }
            this._hardStopCameraTracks();
            VCS.ui?.showToast?.(t('cameraHardStopped'), 'info');
        }, VCS.Media.CAM_HARD_STOP_MS);
    }

    async ensureCameraTrackForEnable() {
        if (this._hasLiveVideoTrack(VCS.state.baseStream)) {
            VCS.state.camHardStopped = false;
            return true;
        }

        let videoTrack = null;
        try {
            const s = await navigator.mediaDevices.getUserMedia({ video: this.getVideoConstraints(), audio: false });
            videoTrack = s.getVideoTracks()[0] || null;
        } catch (err) {
            if (VCS.state.selectedCamId) {
                VCS.state.selectedCamId = null;
                localStorage.removeItem('last-cam-id');
                try {
                    const s2 = await navigator.mediaDevices.getUserMedia({ video: this.getVideoConstraints(), audio: false });
                    videoTrack = s2.getVideoTracks()[0] || null;
                } catch (err2) {
                    console.error('Video-Reaktivierung fehlgeschlagen:', err2);
                    VCS.ui?.showToast?.(t('mediaError'), 'error');
                    return false;
                }
            } else {
                console.error('Video-Reaktivierung fehlgeschlagen:', err);
                VCS.ui?.showToast?.(t('mediaError'), 'error');
                return false;
            }
        }

        if (!videoTrack) {
            VCS.ui?.showToast?.(t('mediaError'), 'error');
            return false;
        }

        if (!VCS.state.baseStream) {
            VCS.state.baseStream = new MediaStream();
        }
        VCS.state.baseStream.getVideoTracks().forEach(tr => {
            try { VCS.state.baseStream.removeTrack(tr); } catch (_) {}
            try { tr.stop(); } catch (_) {}
        });
        VCS.state.baseStream.addTrack(videoTrack);

        await VCS.blur?.rebuild?.();
        const activeTrack = VCS.state.localStream?.getVideoTracks()[0] || videoTrack;
        VCS.webrtc?.replacePeerVideoTrack?.(activeTrack);
        VCS.state.camHardStopped = false;
        return true;
    }

    // ── Preview-Cam-Off-Name ──────────────────────────────────

    updatePreviewCamOffName() {
        const name = this._view.regNameInput.val().trim()
            || VCS.state.myName
            || VCS.state.chatMyName;
        this._view.previewCamOffName.text(name);
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // Initiale Werte setzen (Handler sind in call-controls.js)
        this._view.audioModeSelect.val(VCS.state.audioMode);
        this._view.networkQualitySelect.val(VCS.state.networkQuality);
    }

    // ── Private Methoden ─────────────────────────────────────

    _hasLiveVideoTrack(stream) {
        return !!stream?.getVideoTracks().some(tr => tr.readyState === 'live');
    }

    _clearCamHardStopTimer() {
        if (VCS.state.camOffStopTimer) {
            clearTimeout(VCS.state.camOffStopTimer);
            VCS.state.camOffStopTimer = null;
        }
    }

    _hardStopCameraTracks() {
        const stop = (tr) => {
            if (!tr || tr.readyState === 'ended') {
                return;
            }
            try { tr.stop(); } catch (_) {}
        };
        VCS.state.baseStream?.getVideoTracks().forEach(stop);
        VCS.state.localStream?.getVideoTracks().forEach(stop);
        stop(VCS.state.processedVideoTrack);
        VCS.state.camHardStopped = true;
    }

    async _populateDevices() {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cams    = devices.filter(d => d.kind === 'videoinput');
        const mics    = devices.filter(d => d.kind === 'audioinput');

        const activeCam = VCS.state.baseStream?.getVideoTracks()[0]?.getSettings()?.deviceId;
        const activeMic = VCS.state.baseStream?.getAudioTracks()[0]?.getSettings()?.deviceId;

        const build = ($sel, list, fallbackKey, activeId) => {
            $sel.empty();
            list.forEach(d => {
                const label = this._cleanLabel(d.label) || t(fallbackKey);
                $sel.append($('<option>').val(d.deviceId).text(label)
                    .prop('selected', d.deviceId === activeId));
            });
        };

        build(this._view.camSelect, cams, 'camera',      activeCam);
        build(this._view.micSelect, mics, 'microphone',  activeMic);
    }

    _cleanLabel(label) {
        if (!label) {
            return '';
        }
        return label
            .replace(/^\s*(Default|Communications)\s*-\s*/i, '')
            .replace(/\s*\(default\)\s*$/i, '')
            .trim();
    }
};
